import { useState, useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import {
  Calendar,
  Phone,
  Building,
  Stethoscope,
  User,
  Clock,
  CheckCircle,
} from "lucide-react"

import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import { Badge } from "../components/ui/badge"

export default function BookAppointment() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    firstName: "",
    phone: "",
    email: "",
    branch: searchParams.get("location") || "",
    department: searchParams.get("specialty") || "",
    doctor: searchParams.get("doctor") || "",
    date: "",
    time: "",
    reason: ""
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const params = new URLSearchParams()
    if (formData.branch) params.append('location', formData.branch)
    if (formData.department) params.append('specialty', formData.department)
    if (formData.doctor) params.append('doctor', formData.doctor)
    
    navigate(`/my-bookings${params.toString() ? '?' + params.toString() : ''}`)
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-primary/10 to-primary/20">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                <Calendar className="w-4 h-4 mr-1" />
                Book Appointment
              </Badge>
              <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Schedule Your <span className="text-primary">Appointment</span>
              </h1>
              <p className="mb-8 text-lg text-gray-700">
                Book an appointment with our expert doctors and get the best healthcare services at Pranaam Hospitals.
              </p>
            </div>
          </div>
        </section>

        {/* Appointment Form */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-2xl mx-auto">
              <Card className="shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold mb-6 text-center">Book Your Appointment</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Enter your full name"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Enter your phone number"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                      />
                    </div>

                    {/* Appointment Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Branch *
                        </label>
                        <select
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          value={formData.branch}
                          onChange={(e) => handleInputChange('branch', e.target.value)}
                        >
                          <option value="">Select Branch</option>
                          <option value="madinaguda">Madinaguda</option>
                          <option value="nallagandla">Nallagandla</option>
                          <option value="isnapur">Isnapur</option>
                          <option value="zaheerabad">Zaheerabad</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Department *
                        </label>
                        <select
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          value={formData.department}
                          onChange={(e) => handleInputChange('department', e.target.value)}
                        >
                          <option value="">Select Department</option>
                          <option value="cardiology">Cardiology</option>
                          <option value="neurology">Neurology</option>
                          <option value="orthopedics">Orthopedics</option>
                          <option value="gynecology">Gynecology</option>
                          <option value="pediatrics">Pediatrics</option>
                          <option value="general-medicine">General Medicine</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Doctor
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        value={formData.doctor}
                        onChange={(e) => handleInputChange('doctor', e.target.value)}
                      >
                        <option value="">Any Available Doctor</option>
                        <option value="dr-sharma">Dr. Priya Sharma</option>
                        <option value="dr-patel">Dr. Rajesh Patel</option>
                        <option value="dr-gupta">Dr. Meena Gupta</option>
                        <option value="dr-singh">Dr. Vikram Singh</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Date *
                        </label>
                        <input
                          type="date"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          value={formData.date}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Time *
                        </label>
                        <select
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          value={formData.time}
                          onChange={(e) => handleInputChange('time', e.target.value)}
                        >
                          <option value="">Select Time</option>
                          <option value="09:00 AM">09:00 AM</option>
                          <option value="10:00 AM">10:00 AM</option>
                          <option value="11:00 AM">11:00 AM</option>
                          <option value="02:00 PM">02:00 PM</option>
                          <option value="03:00 PM">03:00 PM</option>
                          <option value="04:00 PM">04:00 PM</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Visit
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Please describe your symptoms or reason for visit"
                        value={formData.reason}
                        onChange={(e) => handleInputChange('reason', e.target.value)}
                      />
                    </div>

                    <Button type="submit" className="w-full gap-2 py-3">
                      <Calendar className="w-5 h-5" />
                      Book Appointment
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-16 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8 text-center">Need Help? Contact Us</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="text-center">
                  <CardContent className="p-6">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Phone className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold mb-2">Call Us</h3>
                    <p className="text-gray-600 mb-4">Speak with our appointment team</p>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="tel:+919966060000">+91 99660 60000</Link>
                    </Button>
                  </CardContent>
                </Card>

                <Card className="text-center">
                  <CardContent className="p-6">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Building className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold mb-2">Visit Us</h3>
                    <p className="text-gray-600 mb-4">Walk-in appointments available</p>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/locations">Find Locations</Link>
                    </Button>
                  </CardContent>
                </Card>

                <Card className="text-center">
                  <CardContent className="p-6">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Clock className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold mb-2">Emergency</h3>
                    <p className="text-gray-600 mb-4">24/7 emergency services</p>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="tel:+919848598285">Emergency Line</Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}